/**
 * BrightPath - ExpenseTrendChart Component
 * BP-04-04: Expenses Frontend
 */

import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Area,
  AreaChart,
} from 'recharts';
import type { ExpenseTrend } from '../types';
import { formatCurrency } from '@/core/lib/utils';

interface ExpenseTrendChartProps {
  data: ExpenseTrend[];
  height?: number;
  showOutOfPocket?: boolean;
  showInsurance?: boolean;
  areaChart?: boolean;
}

export function ExpenseTrendChart({
  data,
  height = 300,
  showOutOfPocket = true,
  showInsurance = true,
  areaChart = false,
}: ExpenseTrendChartProps) {
  const chartData = data.map((item) => ({
    date: new Date(item.date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
    fullDate: item.date,
    total: item.total,
    outOfPocket: item.outOfPocket,
    insuranceCovered: item.insuranceCovered,
    count: item.count,
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white px-4 py-3 rounded-lg shadow-lg border border-warm-gray/10">
          <p className="font-medium text-navy mb-2">{label}</p>
          <div className="space-y-1">
            <p className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-sage" />
              <span className="text-sm text-warm-gray">Total:</span>
              <span className="font-semibold text-navy">{formatCurrency(data.total)}</span>
            </p>
            {showOutOfPocket && (
              <p className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-terracotta" />
                <span className="text-sm text-warm-gray">Out of Pocket:</span>
                <span className="font-semibold text-navy">
                  {formatCurrency(data.outOfPocket)}
                </span>
              </p>
            )}
            {showInsurance && (
              <p className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-sm text-warm-gray">Insurance:</span>
                <span className="font-semibold text-navy">
                  {formatCurrency(data.insuranceCovered)}
                </span>
              </p>
            )}
            <p className="text-xs text-warm-gray mt-1">
              {data.count} expense{data.count !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  if (data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-warm-gray"
        style={{ height }}
      >
        No trend data available
      </div>
    );
  }

  const ChartComponent = areaChart ? AreaChart : LineChart;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ChartComponent data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E5E0DB" vertical={false} />
        <XAxis
          dataKey="date"
          axisLine={false}
          tickLine={false}
          tick={{ fill: '#8B8178', fontSize: 12 }}
          dy={10}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fill: '#8B8178', fontSize: 12 }}
          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
          dx={-10}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          verticalAlign="top"
          height={36}
          formatter={(value) => (
            <span className="text-sm text-navy capitalize">{value.replace(/([A-Z])/g, ' $1').trim()}</span>
          )}
        />

        {areaChart ? (
          <>
            <defs>
              <linearGradient id="totalGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#7A9E7E" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#7A9E7E" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="oopGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#C4785A" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#C4785A" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="total"
              stroke="#7A9E7E"
              strokeWidth={2}
              fill="url(#totalGradient)"
              name="Total"
            />
            {showOutOfPocket && (
              <Area
                type="monotone"
                dataKey="outOfPocket"
                stroke="#C4785A"
                strokeWidth={2}
                fill="url(#oopGradient)"
                name="Out of Pocket"
              />
            )}
          </>
        ) : (
          <>
            <Line
              type="monotone"
              dataKey="total"
              stroke="#7A9E7E"
              strokeWidth={3}
              dot={{ fill: '#7A9E7E', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, strokeWidth: 0 }}
              name="Total"
            />
            {showOutOfPocket && (
              <Line
                type="monotone"
                dataKey="outOfPocket"
                stroke="#C4785A"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: '#C4785A', strokeWidth: 2, r: 3 }}
                activeDot={{ r: 5, strokeWidth: 0 }}
                name="Out of Pocket"
              />
            )}
            {showInsurance && (
              <Line
                type="monotone"
                dataKey="insuranceCovered"
                stroke="#6B8DD6"
                strokeWidth={2}
                dot={{ fill: '#6B8DD6', strokeWidth: 2, r: 3 }}
                activeDot={{ r: 5, strokeWidth: 0 }}
                name="Insurance Covered"
              />
            )}
          </>
        )}
      </ChartComponent>
    </ResponsiveContainer>
  );
}

export default ExpenseTrendChart;
